import * as React from "react";
import { useParams } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";

import OEContext from "../../layouts/OEContext";
import UserContext from "../../layouts/UserContext";
import { parseServiceValidationError } from "../../data/helpers/webHelper";
import {
  useMutateSaveClientRateBandOverride,
  useQueryGetClientRateBandOverrideHistory,
  useQueryGetClientRateBandOverrides,
} from "../../data/services/clientAdminService";

const tableCellStyle = {
  borderBottom: "1px solid #e5e7eb",
  padding: "12px 10px",
  verticalAlign: "middle",
};

const tableHeaderStyle = {
  ...tableCellStyle,
  borderBottom: "2px solid #d1d5db",
  fontWeight: 700,
  background: "#f8fafc",
};

const benefitGroupColumnStyle = {
  width: 110,
  whiteSpace: "nowrap",
};

const currentRateGroupsColumnStyle = {
  width: 250,
};

const effectiveDateColumnStyle = {
  width: 132,
  whiteSpace: "nowrap",
  textAlign: "center",
};

const lastUpdateColumnStyle = {
  width: 155,
  whiteSpace: "nowrap",
  textAlign: "center",
};

const actionColumnStyle = {
  width: 140,
  whiteSpace: "nowrap",
};

const planColumnStyle = {
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const planIdListStyle = {
  display: "inline-flex",
  flexWrap: "wrap",
  gap: 6,
  verticalAlign: "middle",
};

const planIdBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "1px 6px",
  borderRadius: 999,
  background: "#e0f2fe",
  border: "1px solid #bae6fd",
  color: "#075985",
  fontSize: 10,
  fontWeight: 500,
  lineHeight: 1.2,
};

const overrideListStyle = {
  display: "grid",
  gap: 6,
  alignItems: "start",
  maxHeight: 132,
  overflowY: "auto",
  paddingRight: 4,
  scrollbarGutter: "stable",
};

const dateInputStyle = {
  width: 118,
  minWidth: 118,
  padding: "8px 10px",
  textAlign: "center",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  background: "#f8fafc",
  color: "#0f172a",
};

const hiddenInputStyle = {
  position: "absolute",
  opacity: 0,
  width: 1,
  height: 1,
  pointerEvents: "none",
};

const cardStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 8,
  overflow: "hidden",
  background: "#ffffff",
};

const HISTORY_PAGE_SIZE = 20;

const ClientRateBandOverrideEditor = () => {
  const { clientId } = useParams();
  const oePeriod = React.useContext(OEContext);
  const { loggedInUser } = React.useContext(UserContext);
  const isAdmin = Boolean(loggedInUser?.isAdmin);

  const [drafts, setDrafts] = React.useState({});
  const [message, setMessage] = React.useState(null);
  const [savingBenefitGroup, setSavingBenefitGroup] = React.useState(null);
  const [historyBenefitGroup, setHistoryBenefitGroup] = React.useState(null);
  const [historyPage, setHistoryPage] = React.useState(1);

  const overridesQuery = useQueryGetClientRateBandOverrides(
    oePeriod?.oePeriodId,
    clientId
  );
  const historyQuery = useQueryGetClientRateBandOverrideHistory(
    oePeriod?.oePeriodId,
    clientId,
    historyBenefitGroup,
    historyPage,
    HISTORY_PAGE_SIZE
  );
  const saveMutation = useMutateSaveClientRateBandOverride(
    oePeriod?.oePeriodId,
    clientId
  );

  const rows = React.useMemo(
    () => overridesQuery.data?.data || [],
    [overridesQuery.data]
  );
  // History endpoint now returns a paged envelope: { items, pageNumber, totalCount, totalPages }.
  const historyPageData = historyQuery.data?.data || {};
  const historyRows = historyPageData.items || [];
  const historyTotalPages = historyPageData.totalPages || 0;
  const historyTotalCount = historyPageData.totalCount || 0;

  React.useEffect(() => {
    const nextDrafts = {};

    rows.forEach((row) => {
      nextDrafts[row.benefitGroup] = {
        rateGroups: getSelectedRateGroups(row),
        effectiveDate: toDateInputValue(
          row.overrideEffectiveDate || row.oePeriodEffectiveDate
        ),
      };
    });

    setDrafts(nextDrafts);
  }, [rows]);

  React.useEffect(() => {
    if (!message) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setMessage(null);
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [message]);

  if (!oePeriod?.oePeriodId) {
    return <CenteredTabState>Select an OE period to manage rate band overrides.</CenteredTabState>;
  }

  if (overridesQuery.isLoading) {
    return (
      <CenteredTabState>
        <Loader size="large" type="infinite-spinner" />
      </CenteredTabState>
    );
  }

  if (overridesQuery.isError) {
    return (
      <CenteredTabState>
        Unable to load local rate band override data for this client.
      </CenteredTabState>
    );
  }

  if (!isAdmin) {
    return (
      <CenteredTabState>
        Rate band overrides are available to administrators only.
      </CenteredTabState>
    );
  }

  if (!rows.length) {
    return (
      <CenteredTabState>
        No medical benefit groups were found for this client in the selected OE period.
      </CenteredTabState>
    );
  }

  const handleDraftChange = (benefitGroup, field, value) => {
    setDrafts((current) => ({
      ...current,
      [benefitGroup]: {
        ...current[benefitGroup],
        [field]: value,
      },
    }));
  };

  const selectRateGroup = (benefitGroup, rateGroup) => {
    setDrafts((current) => ({
      ...current,
      [benefitGroup]: {
        ...(current[benefitGroup] || {}),
        rateGroups: [rateGroup],
      },
    }));
  };

  const handleSave = async (row) => {
    const draft = drafts[row.benefitGroup] || {};
    const normalizedDraftRateGroups = normalizeRateGroupList(draft.rateGroups);

    setSavingBenefitGroup(row.benefitGroup);
    setMessage(null);

    try {
      await saveMutation.mutateAsync({
        clientId,
        oePeriodId: oePeriod.oePeriodId,
        benefitGroup: row.benefitGroup,
        rateGroups: normalizedDraftRateGroups,
        rateGroup: normalizedDraftRateGroups[0] || null,
        effectiveDate: draft.effectiveDate || toDateInputValue(row.oePeriodEffectiveDate),
      });

      setMessage({
        type: "success",
        text: `Saved client rate group override for ${row.benefitGroup}.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: getMutationErrorMessage(error),
      });
    } finally {
      setSavingBenefitGroup(null);
    }
  };

  const openHistory = (benefitGroup) => {
    setHistoryPage(1);
    setHistoryBenefitGroup(benefitGroup);
  };

  const closeHistory = () => {
    setHistoryBenefitGroup(null);
    setHistoryPage(1);
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <div>
          <h3 style={{ marginBottom: 6 }}>Local Rate Band Overrides</h3>
          <p style={{ margin: 0, color: "#475569" }}>
            Select which client rate group should stay active for each benefit group. Only one rate group can be selected, and it is saved as the active local rate group for this client and OE period.
          </p>
        </div>
        {message ? (
          <div
            style={{
              borderRadius: 6,
              padding: "10px 12px",
              background: message.type === "error" ? "#fef2f2" : "#ecfdf5",
              color: message.type === "error" ? "#b91c1c" : "#166534",
              border: `1px solid ${message.type === "error" ? "#fecaca" : "#bbf7d0"}`,
            }}
          >
            {message.text}
          </div>
        ) : null}
      </div>

      <div style={cardStyle}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ ...tableHeaderStyle, ...benefitGroupColumnStyle }}>Benefit Group</th>
              <th style={{ ...tableHeaderStyle, ...planColumnStyle }}>Plans</th>
              <th style={{ ...tableHeaderStyle, ...currentRateGroupsColumnStyle }}>
                Client Rate Groups
              </th>
              <th style={{ ...tableHeaderStyle, ...effectiveDateColumnStyle }}>Effective Date</th>
              <th style={{ ...tableHeaderStyle, ...lastUpdateColumnStyle }}>Last Update</th>
              <th style={{ ...tableHeaderStyle, ...actionColumnStyle }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.benefitGroup] || {
                rateGroups: getSelectedRateGroups(row),
                effectiveDate: toDateInputValue(
                  row.overrideEffectiveDate || row.oePeriodEffectiveDate
                ),
              };
              const rateGroupOptions = buildRateGroupOptions(row, draft.rateGroups);
              const isSaving = savingBenefitGroup === row.benefitGroup;

              return (
                <tr key={row.benefitGroup}>
                  <td style={{ ...tableCellStyle, ...benefitGroupColumnStyle }}>
                    <strong>{row.benefitGroup}</strong>
                  </td>
                  <td style={{ ...tableCellStyle, ...planColumnStyle }}>
                    <div>{joinItems(row.planDescriptions)}</div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
                      <span>Plan IDs:</span>{" "}
                      <span style={planIdListStyle}>
                        {(row.planIds || []).length ? (
                          row.planIds.map((planId) => (
                            <span key={planId} style={planIdBadgeStyle}>
                              {planId}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: "#64748b", fontSize: 12 }}>-</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, ...currentRateGroupsColumnStyle }}>
                    <div
                      style={overrideListStyle}
                      role="radiogroup"
                      aria-label={`Current client rate groups for ${row.benefitGroup}`}
                    >
                      {rateGroupOptions.length ? (
                        rateGroupOptions.map((option) => {
                          const inputId = `${row.benefitGroup}-${option}`;
                          const isChecked = (draft.rateGroups || []).includes(option);

                          return (
                            <label
                              key={option}
                              htmlFor={inputId}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 13,
                                position: "relative",
                                cursor: "pointer",
                                color: isChecked ? "#1d4ed8" : "#0f172a",
                                fontWeight: isChecked ? 600 : 400,
                              }}
                            >
                              <input
                                id={inputId}
                                type="radio"
                                name={`rate-group-${row.benefitGroup}`}
                                aria-label={option}
                                checked={isChecked}
                                onChange={() => selectRateGroup(row.benefitGroup, option)}
                                style={hiddenInputStyle}
                              />
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 18,
                                  height: 18,
                                  minWidth: 18,
                                  borderRadius: "50%",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: `1px solid ${isChecked ? "#2563eb" : "#94a3b8"}`,
                                  background: "#ffffff",
                                  boxShadow: isChecked
                                    ? "0 0 0 3px rgba(37, 99, 235, 0.14)"
                                    : "inset 0 1px 2px rgba(15, 23, 42, 0.06)",
                                  transition: "all 120ms ease",
                                }}
                              >
                                {isChecked ? (
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: "#2563eb",
                                    }}
                                  />
                                ) : null}
                              </span>
                              <span>{option}</span>
                            </label>
                          );
                        })
                      ) : (
                        <span style={{ color: "#64748b", fontSize: 12 }}>No rate groups available.</span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, ...effectiveDateColumnStyle }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <input
                        aria-label={`Effective date for ${row.benefitGroup}`}
                        type="date"
                        lang="en-US"
                        value={draft.effectiveDate}
                        onChange={(event) =>
                          handleDraftChange(row.benefitGroup, "effectiveDate", event.target.value)
                        }
                        style={dateInputStyle}
                      />
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, ...lastUpdateColumnStyle }}>
                    <div>{formatDateTime(row.overrideUpdatedAt)}</div>
                  </td>
                  <td style={{ ...tableCellStyle, ...actionColumnStyle }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        themeColor="primary"
                        disabled={isSaving || saveMutation.isLoading}
                        onClick={() => handleSave(row)}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                      <Button onClick={() => openHistory(row.benefitGroup)}>
                          Audit
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {historyBenefitGroup ? (
        <Dialog
          title={`${historyBenefitGroup} Change History`}
          onClose={closeHistory}
          width={"70vw"}
        >
          {historyQuery.isLoading ? (
            <div style={{ padding: 16, display: "flex", justifyContent: "center" }}>
              <Loader size="medium" type="infinite-spinner" />
            </div>
          ) : historyQuery.isError ? (
            <div style={{ padding: 8, color: "#b91c1c" }}>
              Unable to load rate band override history for this benefit group.
            </div>
          ) : !historyRows.length ? (
            <div style={{ padding: 8, color: "#64748b" }}>
              No local rate band changes have been recorded for this benefit group yet.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>When</th>
                  <th style={tableHeaderStyle}>User</th>
                  <th style={tableHeaderStyle}>Action</th>
                  <th style={tableHeaderStyle}>Previous</th>
                  <th style={tableHeaderStyle}>New</th>
                  <th style={tableHeaderStyle}>Result</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((item) => (
                  <tr key={item.auditId}>
                    <td style={tableCellStyle}>{formatDateTime(item.actionTime)}</td>
                    <td style={tableCellStyle}>{item.userName || item.userId}</td>
                    <td style={tableCellStyle}>{formatActionLabel(item.userAction)}</td>
                    <td style={tableCellStyle}>
                      {formatRateGroupValue(
                        item.previousRateGroups,
                        item.previousEffectiveDate,
                        item.previousRateGroup
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {formatRateGroupValue(
                        item.newRateGroups,
                        item.newEffectiveDate,
                        item.newRateGroup
                      )}
                    </td>
                    <td style={tableCellStyle}>{item.actionResult || "Success"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {historyTotalPages > 1 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 12,
                padding: "10px 4px 0",
              }}
            >
              <span style={{ color: "#64748b", fontSize: 13 }}>
                Page {historyPage} of {historyTotalPages} ({historyTotalCount} total)
              </span>
              <Button
                disabled={historyPage <= 1 || historyQuery.isFetching}
                onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
              >
                Previous
              </Button>
              <Button
                disabled={historyPage >= historyTotalPages || historyQuery.isFetching}
                onClick={() =>
                  setHistoryPage((page) => Math.min(historyTotalPages, page + 1))
                }
              >
                Next
              </Button>
            </div>
          ) : null}
          <DialogActionsBar layout={"end"}>
            <Button onClick={closeHistory}>Close</Button>
          </DialogActionsBar>
        </Dialog>
      ) : null}
    </div>
  );
};

const CenteredTabState = ({ children }) => (
  <div
    style={{
      minHeight: 240,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      color: "#475569",
      padding: 20,
    }}
  >
    {children}
  </div>
);

const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${minutes}`;
};

const joinItems = (items) => {
  if (!items?.length) {
    return "-";
  }

  return items.join(", ");
};

const buildRateGroupOptions = (row, draftRateGroups) => {
  const selectedRateGroups = new Set(normalizeRateGroupList(draftRateGroups));

  return [...new Set([
    ...(row.availableRateGroups || []),
    ...(row.currentClientRateGroups || []),
    ...(row.overrideRateGroups || []),
    row.overrideRateGroup,
    ...(draftRateGroups || []),
  ].filter(Boolean))].sort((left, right) => {
    const leftSelected = selectedRateGroups.has(left);
    const rightSelected = selectedRateGroups.has(right);

    if (leftSelected !== rightSelected) {
      return leftSelected ? -1 : 1;
    }

    return left.localeCompare(right);
  });
};

const formatActionLabel = (value) => {
  if (!value) {
    return "-";
  }

  return value
    .replace("RateBandOverride", "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
};

const normalizeRateGroupList = (rateGroups, fallbackRateGroup) => {
  const normalizedRateGroups = Array.isArray(rateGroups)
    ? rateGroups.filter((value) => Boolean(value))
    : [];

  if (normalizedRateGroups.length) {
    return [...new Set(normalizedRateGroups)].sort((left, right) => left.localeCompare(right));
  }

  return fallbackRateGroup ? [fallbackRateGroup] : [];
};

const getSelectedRateGroup = (row) => {
  // Single-selection priority: (1) overrideRateGroup (scalar, latest-created —
  // matches the value downstream consumers apply), (2) alphabetically-first of
  // overrideRateGroups (legacy array, sorted for determinism), (3) first of
  // currentClientRateGroups, (4) null when nothing is available.
  if (row.overrideRateGroup) {
    return row.overrideRateGroup;
  }

  const overrideSelections = normalizeRateGroupList(row.overrideRateGroups);
  if (overrideSelections.length) {
    return overrideSelections[0];
  }

  const currentSelections = normalizeRateGroupList(row.currentClientRateGroups);
  return currentSelections[0] || null;
};

const getSelectedRateGroups = (row) => {
  const selected = getSelectedRateGroup(row);
  return selected ? [selected] : [];
};

const getMutationErrorMessage = (error) => {
  const parsedError = parseServiceValidationError(error);

  if (parsedError.errors.length > 0) {
    return parsedError.errors[0];
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.title) {
    return error.response.data.title;
  }

  if (error?.message) {
    return error.message;
  }

  return "Unable to save the rate band override right now. Please try again.";
};

const formatRateGroupValue = (rateGroups, effectiveDate, fallbackRateGroup) => {
  const normalizedRateGroups = normalizeRateGroupList(rateGroups, fallbackRateGroup);
  if (!normalizedRateGroups.length) {
    return "Cleared";
  }

  const formattedDate = toDateInputValue(effectiveDate);
  const rateGroupLabel = normalizedRateGroups.join(", ");
  return formattedDate ? `${rateGroupLabel} (${formattedDate})` : rateGroupLabel;
};

export default ClientRateBandOverrideEditor;